/**
 * addon-benchmark — customActions catalog.
 *
 * Declares the benchmark endpoints dispatched via
 * `api.addons.custom.mutate({ addonId: 'benchmark', action, input })`.
 *
 * Surface:
 *   - Pipeline tester  — `runPipelineTest` (single or parallel-aggregated
 *                        inference runs against a reference image).
 *   - Decoder perf     — `listDecoderFixtures` / `runDecoderPerformanceTest`
 *                        / `abortDecoderPerformanceTest`.
 *   - History (shared) — `listBenchmarkHistory` returns the discriminated
 *                        `HistoryEntrySchema` union (pipeline + decoder-perf);
 *                        `saveBenchmarkResult` and
 *                        `saveDecoderPerformanceResult` append per-kind.
 *                        `deleteBenchmarkResult` / `clearBenchmarkHistory`
 *                        operate cross-kind by id.
 */
import { z } from 'zod'
import { customAction, defineCustomActions, PipelineRunResultBridge } from '@camstack/types'
import {
  PipelineBenchmarkHistoryEntrySchema,
  PipelineBenchmarkHistoryEntryInputSchema,
  DecoderFixtureSchema,
  DecoderPerfInputSchema,
  DecoderPerfResultSchema,
  DecoderPerfHistoryEntrySchema,
  DecoderPerfHistoryEntryInputSchema,
  HistoryEntrySchema,
  PipelineRunInputSchema,
  PipelineRunBatchInputSchema,
  PipelineRunBatchResultSchema,
  MultiCameraStressInputSchema,
  MultiCameraStressResultSchema,
  SustainedThroughputInputSchema,
  SustainedThroughputResultSchema,
  RunReplayClipInputSchema,
  OrchestrateReplayRunResultSchema,
  UploadReplayClipInputSchema,
  UploadReplayClipResultSchema,
  ListReplayRunsInputSchema,
  StoredReplayRunSchema,
  DiffReplayRunsInputSchema,
  ReplayDiffSchema,
} from './benchmark-schemas.js'

export const benchmarkActions = defineCustomActions({
  // Pipeline tester
  runPipelineTest: customAction(PipelineRunInputSchema, PipelineRunResultBridge, {
    kind: 'mutation',
  }),

  /**
   * Decode a JPEG once on the server and cache the resulting raw RGB
   * buffer keyed by a returned `frameId`. Subsequent `runPipelineTest`
   * calls pass `frameId` instead of `imageBase64` — the addon ships the
   * cached raw frame to `pipelineExecutor.runPipeline` via the `frame`
   * field, mirroring the production camera path (stream-broker delivers
   * decoded RGB to pipeline-runner). Eliminates per-call JPEG decode +
   * 265KB base64 transfer that was dominating bench transport time.
   */
  cacheBenchFrame: customAction(
    z.object({ imageBase64: z.string(), ttlSeconds: z.number().int().positive().optional() }),
    z.object({ frameId: z.string(), width: z.number(), height: z.number(), expiresAt: z.number() }),
    { kind: 'mutation' },
  ),
  releaseBenchFrame: customAction(
    z.object({ frameId: z.string() }),
    z.object({ released: z.boolean() }),
    { kind: 'mutation' },
  ),

  /**
   * Batched pipeline tester. Decodes the JPEG once into RGB on the
   * addon, then forwards N FrameInputs to the target node in a single
   * `pipelineExecutor.runPipelineBatch` cap call. Returns N
   * `FrameResult`s plus aggregate timing.
   *
   * NO UI CALLER — and it is NOT dead. `scripts/bench-pool-ceiling.mts` and
   * `scripts/bench-pipeline-investigation.mts` drive it directly. Do not
   * delete it on the strength of an in-package grep; those scripts break
   * silently and nothing in CI runs them.
   */
  runPipelineBatchTest: customAction(PipelineRunBatchInputSchema, PipelineRunBatchResultSchema, {
    kind: 'mutation',
  }),

  /**
   * Multi-camera stress tester — runs the full multi-camera workload
   * loop in-process (decode JPEG once, then N parallel "cameras" each
   * issuing a configurable mix of batched + single cap calls). Returns
   * only aggregate detection-rate stats. Used to measure the pool's
   * true throughput ceiling without per-call tRPC overhead.
   *
   * NO UI CALLER — and it is NOT dead. `scripts/bench-pool-inprocess.mts`
   * drives it directly. Same warning as `runPipelineBatchTest` above.
   */
  runMultiCameraStressTest: customAction(
    MultiCameraStressInputSchema,
    MultiCameraStressResultSchema,
    { kind: 'mutation' },
  ),

  /**
   * Sustained-throughput ROOT-detection e2e run. Pins ONE frame (decoded
   * once), then feeds the identical frame to root object-detection for
   * `durationSec` seconds — driven by BATCHES (`runPipelineBatch`,
   * `batchSize` frames/call) with `concurrency` calls in flight, fanned out
   * across `nodes`, each on its resolved runtime/model + root-only steps.
   * Returns per-node sustained fps / det/s + latency percentiles on the
   * identical pinned frame. Bounded by N + `abortSustainedThroughput`.
   */
  runSustainedThroughput: customAction(
    SustainedThroughputInputSchema,
    SustainedThroughputResultSchema,
    { kind: 'mutation' },
  ),
  abortSustainedThroughput: customAction(
    z.object({ sessionId: z.string() }),
    z.object({ aborted: z.boolean() }),
    { kind: 'mutation' },
  ),

  /**
   * History (shared across pipeline + decoder-perf entries).
   *
   * `z.object({})`, never `z.void()`: the `addons.custom` envelope insists
   * on an `input` object, and a `z.void()` schema REJECTS `{}` — so the
   * action resolved and could never actually be called, not from a settings
   * button and not from `hub.mjs`. The tabs only got away with it by
   * sending `input: undefined`.
   */
  listBenchmarkHistory: customAction(z.object({}), z.array(HistoryEntrySchema).readonly()),
  saveBenchmarkResult: customAction(
    PipelineBenchmarkHistoryEntryInputSchema,
    PipelineBenchmarkHistoryEntrySchema,
    { kind: 'mutation' },
  ),
  saveDecoderPerformanceResult: customAction(
    DecoderPerfHistoryEntryInputSchema,
    DecoderPerfHistoryEntrySchema,
    { kind: 'mutation' },
  ),
  deleteBenchmarkResult: customAction(
    z.object({ id: z.string() }),
    z.object({ success: z.literal(true) }),
    { kind: 'mutation' },
  ),
  clearBenchmarkHistory: customAction(z.object({}), z.object({ success: z.literal(true) }), {
    kind: 'mutation',
  }),

  // Replay — rigiocare una pipeline vera su una clip fissa. Regia qui,
  // FrameProcessor pass in `addon-post-analysis` via
  // `pipelineAnalytics.runReplayFrameProcessor` (see `replay/replay-wiring.ts`).
  uploadReplayClip: customAction(UploadReplayClipInputSchema, UploadReplayClipResultSchema, {
    kind: 'mutation',
  }),
  runReplayClip: customAction(RunReplayClipInputSchema, OrchestrateReplayRunResultSchema, {
    kind: 'mutation',
  }),
  listReplayRuns: customAction(
    ListReplayRunsInputSchema,
    z.array(StoredReplayRunSchema).readonly(),
  ),
  diffReplayRuns: customAction(DiffReplayRunsInputSchema, ReplayDiffSchema),

  // Decoder perf — fixture discovery + run control.
  // `z.object({})` for the same reason as `listBenchmarkHistory` above.
  listDecoderFixtures: customAction(z.object({}), z.array(DecoderFixtureSchema).readonly()),
  runDecoderPerformanceTest: customAction(DecoderPerfInputSchema, DecoderPerfResultSchema, {
    kind: 'mutation',
  }),
  abortDecoderPerformanceTest: customAction(
    z.object({ sessionId: z.string() }),
    z.object({ aborted: z.boolean() }),
    { kind: 'mutation' },
  ),
})

export type BenchmarkActions = typeof benchmarkActions
